import React, { useEffect } from 'react';
import { Sparkles, Phone, Mail, MapPin, Clock, HelpCircle, ChevronDown } from 'lucide-react';
import WhatsAppIcon from '../../components/WhatsAppIcon';
import GoogleMapsIcon from '../../components/GoogleMapsIcon';
import '../../styles/HomamPortal.css';
import { MOCK_SITE_SETTINGS, getWhatsAppLink } from '../../data/homamData';
import HomamSubNavbar from '../../components/HomamSubNavbar';
import HomamEnquiryForm from '../../components/HomamEnquiryForm';
import HomamFooter from '../../components/HomamFooter';

export function HomamContact() {
  useEffect(() => {
    if (window.location.hash === '#faq') {
      setTimeout(() => {
        const el = document.getElementById('faq');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    document.title = 'Contact & Homam Registration | Sri 27 Nakshatra Maha Homam';
  }, []);

  const whatsappHref = getWhatsAppLink({
    customMessage: 'Namaste Sri Gnana Sai Baba Mandir Team 🙏\nI would like assistance with registration for the 27 Nakshatra Maha Homam.',
    source: 'Contact Page Direct Helpline'
  });

  const faqs = [
    {
      q: 'Can I participate if I cannot physically visit Sri Gnana Sai Baba Mandir?',
      a: 'Yes, absolutely. High-definition live webcasting will be provided for all 27 days. Devotees who register for remote Homam will have their names, Nakshatra, and Gothram chanted by the Vedic scholars during the Ahutis, and consecrated Prasad with Raksha will be dispatched to their address.'
    },
    {
      q: 'What if I do not know my exact Janma Nakshatra (birth star)?',
      a: 'You can reach out to our Mandir Seva Team on WhatsApp with your date of birth, time of birth, and place of birth. Our Vedic astrologers will calculate your exact Janma Nakshatra and guide you to the dedicated day.'
    },
    {
      q: 'Can I register for multiple family members with different Nakshatras?',
      a: 'Yes. You can select "All 27 Days (Maha Homam)" or register individual family members for their respective birth star days in the registration form or directly via WhatsApp.'
    },
    {
      q: 'What are the sacred items offered during the Homam?',
      a: "Each star day features pure organic cow ghee, specific sacred wood (Samithu) belonging to the star's sacred tree, rare Himalayan herbs, lotus flowers, Navadhanyas, and consecrated silk vastram during Purnahuti."
    }
  ];

  return (
    <div className="homam-portal-wrapper">
      <HomamSubNavbar />

      {/* Header */}
      <section className="homam-hero-sec" style={{ padding: '40px 0 50px 0' }}>
        <div className="homam-container">
          <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
            <span>Devotee Assistance & Registration</span>
          </div>

          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', color: '#0f2d5c', fontWeight: 800, margin: '0 0 8px 0' }}>
            Homam & Contact Assistance
          </h1>

          <p style={{ fontFamily: 'Noto Sans Tamil, sans-serif', color: '#b8860b', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 14px 0' }}>
            நேரடி தொடர்புகள் & ஹோம உதவி
          </p>

          <p style={{ maxWidth: 650, margin: '0 auto', fontSize: '0.95rem', color: '#475569', lineHeight: 1.6 }}>
            Our Mandir Seva Team is dedicated to guiding devotees in selecting Janma Nakshatras, organizing Homam, and answering any ritual queries.
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <section style={{ padding: '40px 0 70px 0' }}>
        <div className="homam-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 36, alignItems: 'start' }}>
            {/* Left Column: Form */}
            <div>
              <div style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #EADDC7', padding: 24, boxShadow: '0 8px 30px rgba(0,0,0,0.04)', marginBottom: 24 }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 6px 0' }}>
                  <Sparkles className="w-4 h-4" style={{ color: '#b8860b' }} />
                  <span>Submit Homam Details</span>
                </h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px 0' }}>
                  Fill in your birth star and family details to be included in the sacred Maha Yagna.
                </p>
                <HomamEnquiryForm source="Contact Page" />
              </div>
            </div>

            {/* Right Column: Mandir Sanctum & Helpline Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Location Card */}
              <div style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #EADDC7', padding: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px 0' }}>
                  <MapPin className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Mandir Sanctum Location</span>
                </h3>

                <p style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6, margin: '0 0 10px 0' }}>
                  <strong style={{ display: 'block', color: '#0f2d5c', fontSize: 14.5 }}>{MOCK_SITE_SETTINGS.mandirName}</strong>
                  {MOCK_SITE_SETTINGS.venueAddress}, <br />
                  {MOCK_SITE_SETTINGS.venueCity}
                </p>

                <div style={{ marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <a
                    href={MOCK_SITE_SETTINGS.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: '#b8860b',
                      textDecoration: 'none',
                      padding: '5px 12px',
                      borderRadius: 8,
                      background: '#fef3c7',
                      border: '1px solid #fde68a',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}
                  >
                    <GoogleMapsIcon size={16} />
                    <span>View on Google Maps</span>
                  </a>

                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: '#166534',
                      textDecoration: 'none',
                      padding: '5px 12px',
                      borderRadius: 8,
                      background: '#dcfce7',
                      border: '1px solid #bbf7d0'
                    }}
                  >
                    <WhatsAppIcon size={16} style={{ color: '#166534' }} />
                    <span>WhatsApp Seva Team</span>
                  </a>
                </div>

                <div style={{ borderTop: '1px solid #EADDC7', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12.5, color: '#475569' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#0f2d5c', marginBottom: 4 }}>
                      <Phone className="w-4 h-4" style={{ color: '#b8860b' }} />
                      <span>Direct Helpline Numbers:</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 24 }}>
                      {MOCK_SITE_SETTINGS.phoneNumbers.map((phone, idx) => (
                        <a
                          key={idx}
                          href={`tel:${phone.replace(/\s+/g, '')}`}
                          style={{ color: '#0f2d5c', textDecoration: 'none', fontWeight: 600 }}
                        >
                          📞 {phone}
                        </a>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#0f2d5c', marginBottom: 4 }}>
                      <Mail className="w-4 h-4" style={{ color: '#b8860b' }} />
                      <span>Email IDs:</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 24 }}>
                      {MOCK_SITE_SETTINGS.emails.map((email, idx) => (
                        <a
                          key={idx}
                          href={`mailto:${email}`}
                          style={{ color: '#0f2d5c', textDecoration: 'none', fontWeight: 600 }}
                        >
                          ✉️ {email} {idx === 0 && <span style={{ fontSize: 11, color: '#b8860b', fontWeight: 700 }}>(Main)</span>}
                        </a>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4, borderTop: '1px dashed #EADDC7' }}>
                    <Clock className="w-4 h-4" style={{ color: '#b8860b' }} />
                    <span>Helpline Hours: 6:00 AM – 9:00 PM IST</span>
                  </div>
                </div>
              </div>

              {/* FAQs */}
              <div id="faq" style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #EADDC7', padding: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 14px 0' }}>
                  <HelpCircle className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Frequently Asked Questions</span>
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {faqs.map((faq, idx) => (
                    <details
                      key={idx}
                      style={{ background: '#FAF7F2', borderRadius: 14, border: '1px solid #EADDC7', padding: 12, fontSize: 12.5, cursor: 'pointer' }}
                    >
                      <summary style={{ fontWeight: 700, color: '#0f2d5c', display: 'flex', justifyContent: 'space-between', alignItems: 'center', listStyle: 'none' }}>
                        <span>{faq.q}</span>
                        <ChevronDown className="w-4 h-4" style={{ color: '#b8860b' }} />
                      </summary>
                      <p style={{ margin: '8px 0 0 0', paddingTop: 8, borderTop: '1px solid #EADDC7', color: '#475569', lineHeight: 1.5 }}>
                        {faq.a}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HomamFooter />
    </div>
  );
}

export default HomamContact;
